import type { AccessGroup } from '@/api/auth';

/**
 * The two group questions §6.3's controls ask, each against the row of
 * `backend/tests/test_permission_matrix.py` that answers it.
 *
 * ⚠️ **These decide what is *rendered*, never what may be *read*** (the standing obligation, and
 * `RequireGroup`'s docblock says the same). The API refuses on its own in both cases; a client-side
 * check that vanished would cost a 403 on a request, not access to anything. What it buys is §5's
 * property — *"not by disabling items, but by not rendering them"* — and one request that is not
 * sent at all, because for an Interviewer it is a refusal by design rather than a failure.
 */

/**
 * Whether this viewer may read the vacancy catalogue, `GET /api/v1/roles/`.
 *
 * The matrix row is `Route("get", ROLES, None, (401, 403, 200, 200, 403, 403))` over the columns
 * *anon · Interviewer · Recruiter · Admin · Superuser · Superuser+Admin* — so an **Interviewer is
 * 403**, and `backend/CONTEXT.md`'s Group table says the same in words: they reach neither
 * catalogue.
 *
 * ⚠️ The consequence is the whole shape of `VacancyFilter`: for an Interviewer the filter's options
 * are built from the rows already on their screen, and the request is **not issued**. Catching the
 * 403 and hiding the control instead would make a refusal by design indistinguishable from a broken
 * deployment.
 */
export const mayReadVacancyCatalogue = (groups: AccessGroup[]): boolean =>
  groups.includes('Admin') || groups.includes('Recruiter');

/**
 * Whether **Add candidate** is rendered at all (§6.3: *"Add candidate (Recruiter, Admin)"*).
 *
 * `POST /people/` declares `CANDIDATE_WRITE_ACCESS`, whose `group_names` the matrix pins as
 * `(GROUP_ADMIN, GROUP_RECRUITER)` — an Interviewer is 403. So they are shown no button: §5 hides an
 * action a group may not use rather than disabling it, and an empty screen with a disabled primary
 * action reads as a broken product rather than as a boundary.
 */
export const mayAddCandidate = (groups: AccessGroup[]): boolean =>
  groups.includes('Admin') || groups.includes('Recruiter');

/**
 * Whether §6.5's write controls are rendered at all — **Edit** and the three status actions
 * (TASK-066).
 *
 * Four matrix rows, and they are the same row four times over: `Route("patch", PERSON, …,
 * (401, 403, 200, 200, 403, 403))` and `Route("post", f"{CONSIDERATION}make-offer/" | "hire/" |
 * "reject/", None, (401, 403, 200, 200, 403, 403))` over the columns *anon · Interviewer · Recruiter
 * · Admin · Superuser · Superuser+Admin*. All four declare the same dependency `POST /people/`
 * declares (`CANDIDATE_WRITE_ACCESS`), so an **Interviewer is 403** on every write this card can
 * make: they read a card they were assigned to and change nothing on it.
 *
 * ⚠️ It is deliberately a **second** predicate rather than a reuse of `mayAddCandidate`, even though
 * the body is identical today: the two answer about different routes, and this surface has already
 * proved that such routes diverge — `POST /people/{id}/anonymize/` is `ANONYMIZE_ACCESS`, Admin
 * only, and refuses the very Recruiter who may create and edit the person. That divergence is no
 * longer hypothetical: it is `mayAnonymizePerson` below, whose body is genuinely different.
 * Collapsing these two into one would make the next narrowing a silent widening here.
 *
 * ⚠️ **It decides what is rendered, never what may be read or written** (the standing obligation of
 * 2026-07-25). The server refuses on its own; what this buys is §5's property — an action a group may
 * not use is not rendered rather than disabled — and no request that is a refusal by design.
 */
export const mayWriteAboutPerson = (groups: AccessGroup[]): boolean =>
  groups.includes('Admin') || groups.includes('Recruiter');

/**
 * Whether §6.5's **Anonymize** item is rendered at all — the product's only erasure path (ADR 0009,
 * feedback protection #6, TASK-067).
 *
 * **One matrix row, and it is the narrowest on this surface:**
 * `Route("post", f"{PERSON}anonymize/", None, (401, 403, 403, 200, 403, 403))` — `backend/tests/
 * test_permission_matrix.py`, over the columns *anon · Interviewer · Recruiter · Admin · Superuser ·
 * Superuser+Admin*. The **Recruiter column is 403**, which is the whole reason this predicate is not
 * `mayWriteAboutPerson`: the very Recruiter who may create and edit this person may not erase them.
 * The route declares `ANONYMIZE_ACCESS` and the matrix's own comment beside the row says why it sits
 * last (the erasure closes every write about that person, so walking it earlier would make the rows
 * below read as if those routes refused an Admin).
 *
 * ⚠️ **The client gate is never the evidence** (the standing obligation of 2026-07-25). The server
 * refuses a Recruiter on its own, and the row above is where that is proven; what this buys is §5's
 * property — an action a group may not use is **not rendered** rather than disabled, and a disabled
 * *Anonymize* would tell a Recruiter that the capability exists and is being withheld from them.
 */
export const mayAnonymizePerson = (groups: AccessGroup[]): boolean => groups.includes('Admin');

/**
 * Whether §6.5's **acknowledgement** of an import's possible-duplicate warning is rendered at all
 * (ADR 0021, TASK-179).
 *
 * **One matrix row**: `Route("post", f"{POSSIBLE_DUPLICATE}acknowledge/", None, (401, 403, 200, 200,
 * 403, 403))` — `backend/tests/test_permission_matrix.py`, over the columns *anon · Interviewer ·
 * Recruiter · Admin · Superuser · Superuser+Admin*. The route declares the candidate surface's
 * `CANDIDATE_WRITE_ACCESS`, so an **Interviewer is 403 even for a candidate they are assigned to**:
 * reach is not permission, and closing this warning is a judgement about the tenant's candidate
 * records.
 *
 * ⚠️ **A sixth predicate rather than a reuse of `mayWriteAboutPerson`, whose body is identical
 * today** — this file's standing rule, and the one that has already paid for itself once
 * (`mayAnonymizePerson`). The two answer about different routes, and this one is the newest surface
 * on the card.
 *
 * ⚠️ **It gates the action and never the warning.** The sentence, the matched keys and the link to the
 * other card render for **every** reader, an `Interviewer` included: ADR 0021's list of what breaks
 * when two cards are one human is mostly a list of things that happen to an interviewer — the earlier
 * feedback does not surface, and the blind gate splits with the card — so withholding the warning from
 * them would hide it from the person it costs the most.
 */
export const mayAcknowledgePossibleDuplicate = (groups: AccessGroup[]): boolean =>
  groups.includes('Admin') || groups.includes('Recruiter');

/**
 * Whether this viewer may read the **staff directory**, `GET /api/v1/users/` (backend TASK-090).
 *
 * The matrix row is `Route("get", USERS, None, (401, 403, 200, 200, 403, 403))` over the columns
 * *anon · Interviewer · Recruiter · Admin · Superuser · Superuser+Admin* — so an **Interviewer is
 * 403**, on ADR 0016's reasoning: the list exists to serve assignment and they assign nobody. They are
 * not thereby cut off from colleagues' names — every interview payload they may read carries the
 * interviewer's name — they simply cannot **enumerate** or search staff.
 *
 * ⚠️ It is a **fifth** predicate rather than a reuse of `mayWriteAboutPerson`, whose body is identical
 * today, for this file's standing reason: the two answer about different routes, and this surface has
 * already had one pair diverge (`mayAnonymizePerson`). Scheduling and reading the directory are
 * `SCHEDULE_INTERVIEW_ACCESS` and `STAFF_READ_ACCESS` on the server — two **separate** named
 * dependencies, chosen so that widening who may assign an interview cannot silently widen who may
 * read the tenant's staff list. Collapsing them here would undo that on the client.
 *
 * ⚠️ **It decides what is rendered and what is *requested*, never what may be read.** The read gate is
 * the half that matters here: the hazard is a demotion **without** a re-login (`useSession` has a
 * `staleTime`), which leaves a cached directory entry readable by a viewer who has just lost the
 * right — so the caller gates on the group as well as on `enabled`, exactly as `VacancyFilter` does.
 */
export const mayReadStaffDirectory = (groups: AccessGroup[]): boolean =>
  groups.includes('Admin') || groups.includes('Recruiter');
